// Экран упражнения типа text_fill_bank: текст с пропусками и общий банк слов для вставки.
// Студент выбирает слова из банка и вставляет в пронумерованные пропуски.

import { useState, useMemo, useCallback } from 'react';
import {
  Container, Card, Button, Badge, ProgressBar, Modal, Spinner, Alert,
} from 'react-bootstrap';
import AppNavbar from '../../common/AppNavbar';
import { completeVariant, interruptVariant } from '../../../api/studentApi';

const LEVEL_LABEL   = { 1: 'Beginner', 2: 'Elementary', 3: 'Pre-Intermediate', 4: 'Intermediate' };
const LEVEL_VARIANT = { 1: 'success', 2: 'info', 3: 'primary', 4: 'warning' };

// ── Перемешивание (Fisher-Yates) ──────────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Парсинг текста с %N или _____ плейсхолдерами ──────────────────────────────
// Возвращает массив: { type: 'text', value } | { type: 'blank', pos: '1' }
function parseSentence(sentence) {
  const tokens = [];
  let remaining = sentence ?? '';
  let autoPos = 0;
  let safety = 0;
  while (remaining.length > 0 && safety++ < 500) {
    // Поддерживаем оба формата: %N и _____ (5+ подчёркиваний)
    const match = remaining.match(/^(.*?)(?:%(\d+)|_{5,})(.*)/s);
    if (!match) {
      tokens.push({ type: 'text', value: remaining });
      break;
    }
    if (match[1]) tokens.push({ type: 'text', value: match[1] });
    const pos = match[2] !== undefined ? match[2] : String(++autoPos);
    tokens.push({ type: 'blank', pos });
    remaining = match[3];
  }
  return tokens;
}

// ── Компонент пропуска ────────────────────────────────────────────────────────
function BlankSlot({ pos, filledWord, isActive, onClickEmpty, onClickFilled }) {
  if (filledWord) {
    return (
      <span
        onClick={() => onClickFilled(pos, filledWord)}
        title="Нажмите, чтобы вернуть слово в банк"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          background: '#4f6ef7',
          color: '#fff',
          borderRadius: '0.375rem',
          padding: '2px 10px',
          fontWeight: 600,
          fontSize: '0.9rem',
          cursor: 'pointer',
          verticalAlign: 'middle',
          margin: '0 2px',
          transition: 'opacity 0.15s',
          lineHeight: 1.5,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.82'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        {filledWord}
        <span style={{ fontSize: '0.7rem', opacity: 0.8, lineHeight: 1 }}>×</span>
      </span>
    );
  }

  return (
    <span
      onClick={() => onClickEmpty(pos)}
      title={isActive ? 'Выберите слово из банка' : 'Нажмите, чтобы выбрать это поле'}
      style={{
        display: 'inline-block',
        minWidth: '80px',
        height: '1.75em',
        verticalAlign: 'middle',
        borderRadius: '0.375rem',
        border: isActive ? '2px solid #4f6ef7' : '2px dashed #93b0fb',
        background: isActive ? 'rgba(79,110,247,0.08)' : 'transparent',
        boxShadow: isActive ? '0 0 0 3px rgba(79,110,247,0.15)' : 'none',
        cursor: 'pointer',
        margin: '0 2px',
        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.background = 'rgba(79,110,247,0.06)';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    />
  );
}

// ── Главный компонент ─────────────────────────────────────────────────────────
// Формат sentences для text_fill_bank:
//   sentences["1"] = { sentence: "...%1...%2...", words: {"1": ["w1","w2",...], "2": [...]} }
// Слова из всех позиций собираются в общий банк и показываются перемешанными.
// Ответ: { sentenceId: { blankPos: word } }
export default function StudentTextFillBankScreen({
  variant,
  historyId,
  exerciseId,
  variantId,
  navigate,
  onInterrupt,
  previewMode = false,
}) {
  // Берём первое предложение
  const sentenceEntries = useMemo(
    () => Object.entries(variant.sentences ?? {}).sort(([a], [b]) => Number(a) - Number(b)),
    [variant.sentences],
  );

  const primarySentId = sentenceEntries[0]?.[0] ?? '1';
  const primarySent   = sentenceEntries[0]?.[1] ?? { sentence: '', words: {} };

  // Токены текста
  const tokens = useMemo(() => parseSentence(primarySent.sentence), [primarySent.sentence]);

  const wordsBank = primarySent.words ?? {};

  // Перемешиваем все слова из банка в единый список (для text_fill_bank — общий пул)
  const bankWords = useMemo(() => {
    const all = [];
    Object.values(wordsBank).forEach((arr) => {
      if (Array.isArray(arr)) all.push(...arr);
    });
    return shuffle(all);
  }, [variant.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Все позиции пропусков
  const blankPositions = useMemo(
    () => tokens.filter((t) => t.type === 'blank').map((t) => t.pos),
    [tokens],
  );
  const totalBlanks = blankPositions.length;

  // answers: { blankPos → word }
  const [answers, setAnswers]     = useState({});
  const [activeBlank, setActiveBlank] = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showInterruptModal, setShowInterruptModal] = useState(false);
  const [interrupting, setInterrupting]             = useState(false);
  const [showRuleModal, setShowRuleModal]           = useState(false);

  const usedWords     = useMemo(() => new Set(Object.values(answers)), [answers]);
  const answeredCount = Object.keys(answers).length;
  const progress      = totalBlanks > 0 ? Math.round((answeredCount / totalBlanks) * 100) : 0;
  const allFilled     = answeredCount === totalBlanks && totalBlanks > 0;

  // Клик по слову из банка
  const handleWordClick = useCallback((word) => {
    if (usedWords.has(word)) return;
    setAnswers((prev) => {
      if (activeBlank !== null && !prev[activeBlank]) {
        setActiveBlank(null);
        return { ...prev, [activeBlank]: word };
      }
      for (const pos of blankPositions) {
        if (!prev[pos]) {
          return { ...prev, [pos]: word };
        }
      }
      return prev;
    });
  }, [activeBlank, blankPositions, usedWords]);

  const handleClickEmpty = useCallback((pos) => {
    setActiveBlank((prev) => (prev === pos ? null : pos));
  }, []);

  const handleClickFilled = useCallback((pos) => {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[pos];
      return next;
    });
    setActiveBlank(pos);
  }, []);

  // Завершение
  const handleComplete = async () => {
    if (previewMode) {
      if (onInterrupt) onInterrupt();
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const answerPayload = { [primarySentId]: answers };
      const payload = {
        history_id: historyId,
        answer: JSON.stringify(answerPayload),
      };
      const { data: result } = await completeVariant(variantId, payload);
      navigate(
        '/student/exercises/result',
        { state: { result, exerciseId, variantId } },
      );
    } catch {
      setSubmitError('Не удалось отправить ответы. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  // Прерывание
  const handleInterrupt = async () => {
    if (previewMode) {
      setShowInterruptModal(false);
      if (onInterrupt) onInterrupt();
      return;
    }
    setInterrupting(true);
    try {
      await interruptVariant(variantId, { history_id: historyId });
    } catch {
      // 204 No Content — игнорируем
    } finally {
      setInterrupting(false);
      setShowInterruptModal(false);
      if (onInterrupt) {
        onInterrupt();
      } else {
        navigate('/student/exercises');
      }
    }
  };

  const complexityLevel = variant.complexity_level;
  const complexityId    = variant.complexity_level_id;

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)', paddingBottom: '5rem' }}>
        <Container className="py-4" style={{ maxWidth: 800 }}>

          {/* ── Заголовок и мета ── */}
          <div className="mb-4">
            <div className="mb-2">
              <Button
                variant="link"
                size="sm"
                onClick={() => previewMode ? onInterrupt?.() : setShowInterruptModal(true)}
                style={{ padding: 0, color: '#6c757d', textDecoration: 'none', fontWeight: 500, fontSize: '0.875rem' }}
              >
                {previewMode ? '← Закрыть просмотр' : '← К упражнениям'}
              </Button>
            </div>

            {previewMode && (
              <Alert variant="info" className="py-2 px-3 mb-2" style={{ fontSize: '0.85rem', borderRadius: '0.5rem' }}>
                Режим просмотра — результат не сохраняется
              </Alert>
            )}

            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <h5
                  className="fw-bold mb-1"
                  style={{ letterSpacing: '-0.01em', color: '#1e2a4a', lineHeight: 1.35 }}
                >
                  {variant.exercise_name}
                </h5>
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Вариант {variant.variant}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap justify-content-end">
                {complexityLevel ? (
                  <Badge bg={LEVEL_VARIANT[complexityLevel.id] ?? 'secondary'} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {complexityLevel.name}
                  </Badge>
                ) : complexityId ? (
                  <Badge bg={LEVEL_VARIANT[complexityId] ?? 'secondary'} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {LEVEL_LABEL[complexityId] ?? complexityId}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Прогресс ── */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: '0.8125rem', color: '#6c757d', fontWeight: 500 }}>
                Заполнено {answeredCount} из {totalBlanks}
              </span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--bs-primary)', fontWeight: 600 }}>
                {progress}%
              </span>
            </div>
            <ProgressBar
              now={progress}
              style={{ height: '0.375rem', borderRadius: '1rem' }}
              variant={progress === 100 ? 'success' : 'primary'}
            />
          </div>

          {/* ── Блок задания ── */}
          {variant.exercise_task && (
            <Card
              className="border-0 mb-4"
              style={{
                borderRadius: '0.875rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                background: '#fff',
                borderLeft: '4px solid var(--bs-primary)',
              }}
            >
              <Card.Body className="p-4">
                <div
                  className="fw-semibold mb-1"
                  style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
                >
                  Задание
                </div>
                <p className="mb-0 fw-semibold" style={{ color: '#1e2a4a', fontSize: '1rem', lineHeight: 1.5 }}>
                  {variant.exercise_task}
                </p>
              </Card.Body>
            </Card>
          )}

          {/* ── Кнопка грамматического правила ── */}
          {variant.grammar_rule && (
            <div className="mb-3 d-flex justify-content-end">
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => setShowRuleModal(true)}
                style={{ borderRadius: '0.5rem', fontWeight: 500 }}
              >
                Показать правило
              </Button>
            </div>
          )}

          {/* ── Блок текста с пропусками ── */}
          <Card
            className="border-0 mb-4"
            style={{
              borderRadius: '0.875rem',
              boxShadow: 'var(--ewl-shadow-sm)',
              background: '#fff',
            }}
          >
            <Card.Body className="p-4">
              {tokens.length > 0 ? (
                <p
                  style={{
                    fontSize: '1.05rem',
                    lineHeight: 2,
                    color: '#1e2a4a',
                    margin: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {tokens.map((tok, idx) => {
                    if (tok.type === 'text') {
                      return <span key={idx}>{tok.value}</span>;
                    }
                    return (
                      <BlankSlot
                        key={idx}
                        pos={tok.pos}
                        filledWord={answers[tok.pos] ?? null}
                        isActive={activeBlank === tok.pos}
                        onClickEmpty={handleClickEmpty}
                        onClickFilled={handleClickFilled}
                      />
                    );
                  })}
                </p>
              ) : (
                <div className="text-center py-4 text-muted" style={{ fontSize: '0.9rem' }}>
                  Нет данных для отображения
                </div>
              )}
            </Card.Body>
          </Card>

          {/* ── Банк слов ── */}
          <Card
            className="border-0 mb-4"
            style={{
              borderRadius: '0.875rem',
              boxShadow: 'var(--ewl-shadow-sm)',
              background: '#fff',
            }}
          >
            <Card.Body className="p-4">
              <div
                className="fw-semibold mb-3"
                style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}
              >
                Слова для вставки
              </div>

              <div className="d-flex flex-wrap gap-2">
                {bankWords.map((word, idx) => {
                  const used = usedWords.has(word);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleWordClick(word)}
                      disabled={used}
                      style={{
                        background: used ? '#f8f9fa' : '#fff',
                        color: used ? '#adb5bd' : '#1e2a4a',
                        border: `2px solid ${used ? '#e9ecef' : '#dee2e6'}`,
                        borderRadius: '2rem',
                        padding: '6px 18px',
                        cursor: used ? 'default' : 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        lineHeight: 1.4,
                        transition: 'border-color 0.15s, color 0.15s, background 0.15s',
                        outline: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!used) {
                          e.currentTarget.style.borderColor = '#4f6ef7';
                          e.currentTarget.style.color = '#4f6ef7';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!used) {
                          e.currentTarget.style.borderColor = '#dee2e6';
                          e.currentTarget.style.color = '#1e2a4a';
                        }
                      }}
                    >
                      {word}
                    </button>
                  );
                })}
              </div>
            </Card.Body>
          </Card>

          {/* ── Ошибка отправки ── */}
          {submitError && (
            <div
              style={{
                background: '#fff3f3',
                border: '1px solid #f5c6cb',
                borderRadius: '0.75rem',
                padding: '0.875rem 1.25rem',
                color: '#842029',
                fontSize: '0.9rem',
                marginBottom: '1rem',
              }}
            >
              {submitError}
            </div>
          )}

        </Container>
      </div>

      {/* ── Sticky footer ── */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #e9ecef',
          padding: '0.875rem 1rem',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
          zIndex: 1000,
        }}
      >
        <Container style={{ maxWidth: 800 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="outline-secondary"
              onClick={() => previewMode ? onInterrupt?.() : setShowInterruptModal(true)}
              style={{ borderRadius: '0.5rem', fontWeight: 500 }}
            >
              {previewMode ? 'Закрыть просмотр' : 'Прервать'}
            </Button>

            <div className="d-flex align-items-center gap-3">
              {!allFilled && totalBlanks > 0 && (
                <span style={{ fontSize: '0.8125rem', color: '#6c757d' }}>
                  Осталось: {totalBlanks - answeredCount}
                </span>
              )}
              <Button
                onClick={handleComplete}
                disabled={!allFilled || submitting}
                style={{
                  background: allFilled
                    ? 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)'
                    : undefined,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: 600,
                  minWidth: 160,
                  opacity: allFilled ? 1 : 0.55,
                }}
              >
                {submitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Проверяем...</>
                ) : 'Завершить'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* ── Модалка прерывания ── */}
      <Modal show={showInterruptModal} onHide={() => setShowInterruptModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Прервать упражнение?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          <p className="mb-0 text-muted" style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
            Упражнение будет сохранено как незавершённое. Результат показан не будет.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowInterruptModal(false)}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}
          >
            Продолжить
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleInterrupt}
            disabled={interrupting}
            style={{ borderRadius: '0.5rem', fontWeight: 600 }}
          >
            {interrupting ? 'Прерываем...' : 'Да, прервать'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Модалка грамматического правила ── */}
      <Modal show={showRuleModal} onHide={() => setShowRuleModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>
            {variant.grammar_rule?.title ?? 'Грамматическое правило'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <p style={{ lineHeight: 1.7, color: '#1e2a4a', whiteSpace: 'pre-wrap', margin: 0 }}>
            {variant.grammar_rule?.description}
          </p>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowRuleModal(false)}
            style={{ borderRadius: '0.5rem', fontWeight: 500 }}
          >
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
